import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { OfferSource, OfferType, RecordOfferInput } from '../../api/OfferPortalApi';
import { OFFER_SOURCE_LABELS, OFFER_TYPE_LABELS } from './offerDisplay';

/**
 * Entering an offer by hand (Lloyd, Aug 18).
 *
 * "Sometimes, the offer comes in the pre-trial notice. Other times, the court or prosecutor will
 * convey the offer via email. However, there are other times when it is conveyed over the phone or
 * through a hearing that I appear at in person or virtually."
 *
 * Only the first two of those leave anything a parser could read, so this is the primary path
 * rather than a fallback — and when the parsed routes arrive they pre-fill this same form.
 *
 * The deferral fields appear only for a deferral, and the reduced-charge fields only for a
 * conviction. Terms entered against the wrong type would be stored where the wording sent to the
 * client never mentions them.
 */
interface OfferRecordFormProps {
  onSubmit: (input: RecordOfferInput) => Promise<void>;
  saving: boolean;
}

const TYPE_OPTIONS = Object.keys(OFFER_TYPE_LABELS) as OfferType[];
const SOURCE_OPTIONS = Object.keys(OFFER_SOURCE_LABELS) as OfferSource[];

/** Today in the input's own format, so the default is the common case: entered when it arrived. */
function todayIsoDate(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function OfferRecordForm({ onSubmit, saving }: OfferRecordFormProps) {
  const [offerType, setOfferType] = useState<OfferType>('deferral');
  const [source, setSource] = useState<OfferSource>('court_email');
  const [receivedAt, setReceivedAt] = useState(todayIsoDate());
  const [deferralDays, setDeferralDays] = useState('');
  const [deferralFee, setDeferralFee] = useState('');
  const [dscAlso, setDscAlso] = useState(false);
  const [reducedFrom, setReducedFrom] = useState('');
  const [reducedTo, setReducedTo] = useState('');
  const [notes, setNotes] = useState('');

  const isDeferral = offerType === 'deferral';
  const isConviction = offerType === 'conviction';
  // A phone or hearing offer has no document behind it, so these notes are the only account of
  // what was said. Requiring them there, and only there, is the difference that matters.
  const notesRequired = source === 'phone' || source === 'hearing';
  const notesMissing = notesRequired && !notes.trim();

  const submit = async () => {
    await onSubmit({
      offerType,
      source,
      receivedAt: receivedAt ? new Date(`${receivedAt}T12:00:00`).toISOString() : null,
      deferralDays: isDeferral && deferralDays ? Number(deferralDays) : null,
      deferralFeeUsd: isDeferral && deferralFee ? Number(deferralFee) : null,
      dscAlsoRequired: isDeferral ? dscAlso : false,
      reducedFrom: isConviction ? reducedFrom.trim() || null : null,
      reducedTo: isConviction ? reducedTo.trim() || null : null,
      notes: notes.trim() || null,
    });
    setNotes('');
    setReducedFrom('');
    setReducedTo('');
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Record a new offer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        For an offer that came by phone, at a hearing, or a revised offer. It is added to the offers
        above.
      </Typography>

      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Offer type"
            value={offerType}
            onChange={(e) => setOfferType(e.target.value as OfferType)}
            size="small"
            sx={{ minWidth: 260 }}
          >
            {TYPE_OPTIONS.map((value) => (
              <MenuItem key={value} value={value}>
                {OFFER_TYPE_LABELS[value]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="How it reached us"
            value={source}
            onChange={(e) => setSource(e.target.value as OfferSource)}
            size="small"
            sx={{ minWidth: 260 }}
          >
            {SOURCE_OPTIONS.map((value) => (
              <MenuItem key={value} value={value}>
                {OFFER_SOURCE_LABELS[value]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Date received"
            type="date"
            size="small"
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
        </Stack>

        {isDeferral && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Deferral days"
              type="number"
              size="small"
              value={deferralDays}
              onChange={(e) => setDeferralDays(e.target.value)}
              sx={{ maxWidth: 160 }}
            />
            <TextField
              label="Deferral fee (USD)"
              type="number"
              size="small"
              value={deferralFee}
              onChange={(e) => setDeferralFee(e.target.value)}
              sx={{ maxWidth: 180 }}
            />
            <FormControlLabel
              control={<Checkbox checked={dscAlso} onChange={(e) => setDscAlso(e.target.checked)} />}
              label="DSC also required"
            />
          </Stack>
        )}

        {isConviction && (
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Charge lowered from"
                placeholder="15 mph over the limit"
                size="small"
                value={reducedFrom}
                onChange={(e) => setReducedFrom(e.target.value)}
                sx={{ minWidth: 260 }}
              />
              <TextField
                label="Charge lowered to"
                placeholder="6 mph over the limit"
                size="small"
                value={reducedTo}
                onChange={(e) => setReducedTo(e.target.value)}
                sx={{ minWidth: 260 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Fill both when the court is lowering the charge. The letter then explains the reduction and
              recommends accepting it. Leave blank for a conviction with no reduction.
            </Typography>
          </Box>
        )}

        <TextField
          label={notesRequired ? 'What was offered (required)' : 'Notes'}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          required={notesRequired}
          error={notesMissing}
          helperText={
            notesMissing
              ? 'There is no document behind a phone or hearing offer — this note is the only record of the terms.'
              : 'Anything the client should be told that the standard wording does not cover.'
          }
        />

        <Box>
          <Button variant="contained" onClick={submit} disabled={saving || notesMissing}>
            {saving ? 'Saving…' : 'Save offer'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Saving records the offer. Nothing goes to the client until you review and send it.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default OfferRecordForm;
